/**
 * Profile Settings Component
 * Allows users to edit their profile information
 */

import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { updateProfile } from "../../services/auth.service";
import Button from "../common/Button";
import Input from "../common/Input";
import Card from "../ui/Card";

const ProfileSettings: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    full_name: user?.full_name || "",
    phone: user?.phone_number || "",
  });

  // Update form when user data loads/changes
  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || "",
        phone: user.phone_number || "",
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!user?.id) {
      return;
    }

    try {
      setIsSaving(true);
      setErrorMessage("");
      setSuccessMessage("");

      await updateProfile(user.id, {
        full_name: formData.full_name,
        // profiles.phone_number — writing `phone` (no such column) made every save fail.
        // null (not undefined) so clearing the field actually clears it.
        phone_number: formData.phone.trim() || null,
      });

      // Refresh user data in context
      await refreshUser();

      setSuccessMessage("Profile updated successfully");
      setIsEditing(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error: any) {
      console.error("Error updating profile:", error);
      setErrorMessage(error.message || "Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form to current user data
    setFormData({
      full_name: user?.full_name || "",
      phone: user?.phone_number || "",
    });
    setIsEditing(false);
    setErrorMessage("");
  };

  // Format role for display
  const formatRole = (role: string | undefined) => {
    if (!role) {
      return "N/A";
    }
    // Convert snake_case to Title Case
    return role
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          Profile Information
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Update your personal information and contact details
        </p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="p-3 rounded-lg bg-green-50 border border-green-200">
          <p className="text-sm text-green-800">{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}

      <Card padding="lg">
        <div className="space-y-4">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            {isEditing ? (
              <Input
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="Enter your full name"
                fullWidth
              />
            ) : (
              <p className="text-gray-900 py-2">
                {user?.full_name || "Not set"}
              </p>
            )}
          </div>

          {/* Email (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <p className="text-gray-900 py-2">{user?.email}</p>
            <p className="text-xs text-gray-500 mt-1">
              Email cannot be changed. Contact your administrator if you need to
              update it.
            </p>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number (Optional)
            </label>
            {isEditing ? (
              <Input
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                fullWidth
              />
            ) : (
              <p className="text-gray-900 py-2">
                {user?.phone_number || "Not set"}
              </p>
            )}
            {isEditing && (
              <p className="text-xs text-gray-500 mt-1">
                Used for SMS notifications
              </p>
            )}
          </div>

          {/* Role (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <p className="text-gray-900 py-2">{formatRole(user?.role)}</p>
            <p className="text-xs text-gray-500 mt-1">
              Your role determines your access permissions. Contact your
              administrator to change roles.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            {isEditing ? (
              <>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  isLoading={isSaving}
                  disabled={isSaving}
                >
                  Save Changes
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="primary" onClick={() => setIsEditing(true)}>
                Edit Profile
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProfileSettings;
